import { zarazTrack } from './zaraz'

/**
 * Per-search and per-account ad conversions, dispatched through Zaraz.
 *
 * ⚠️ THE TWO EVENT NAMES BELOW ARE CROSS-PLATFORM CONTRACT, NOT GOOGLE-ONLY.
 * Despite this file's name and the `manual2` suffix (a Google Ads artifact —
 * see DUP-2080), `dupe_performed_manual2` and `dupe_account_creation2` are the
 * canonical per-search / per-account Zaraz events, and more than one ad
 * platform's managed component is triggered on them by NAME at the Cloudflare
 * edge:
 *
 *   dupe_performed_manual2  → Google Ads conversion action
 *   dupe_account_creation2  → Google Ads conversion action
 *                           → TikTok `CompleteRegistration` (DUP-2125)
 *
 * TikTok's `Search` used to ride `dupe_performed_manual2` too (DUP-2125). Since
 * DUP-2261 it rides its own per-user-per-DAY event, `dupe_performed_daily`
 * (`tiktokConversions.ts`), because marketing asked TikTok to match Meta's
 * cadence while Google stays per-search. Do NOT re-add a TikTok `Search`
 * trigger on `dupe_performed_manual2` in Cloudflare: TikTok would count every
 * search again, and both events carry the same payload shape, so nothing
 * would look wrong.
 *
 * Renaming either string, or dropping `event_id` from the payload, breaks
 * those platforms SILENTLY: the triggers stop matching at the edge, no error
 * is raised anywhere in this repo, and no test outside this file fails. The
 * TikTok mapping lives in Cloudflare, not in git — see
 * docs/references/tiktok-conversion-tracking.md before touching a name.
 *
 * Meta is deliberately NOT a consumer of this Zaraz event, but it DOES share
 * its transaction id — see `fireOnce` below. Meta's conversion is fired from
 * the same call sites over its own transports: the browser pixel
 * (`metaConversions.ts`, live) and the `Performed Dupe Ads Conversion`
 * PostHog event (`metaAdsConversion.ts`, prepared but not yet delivering).
 * The Zaraz-migration Motion 2 plan used to map THIS event to Meta at a
 * managed component, which would have made it three platforms; that plan is
 * retired (DUP-2091, 2026-09-03). Do not re-add a Meta trigger here without
 * retiring those first, or Meta double-counts. See
 * docs/features/active/zaraz-migration/TODO.md.
 *
 * ⚠️ AND THE BIGGER SILENT FAILURE IS UPSTREAM OF THE NAMES:
 * `NEXT_PUBLIC_ZARAZ_TRACK_ENABLED`. `zarazTrack` returns false immediately
 * unless that var is exactly the string `'true'` (see `./zaraz`), and being a
 * `NEXT_PUBLIC_` var it is baked in at BUILD time. Unset, misspelled, or
 * missing from a Vercel environment and every conversion here stops for every
 * platform at once — no error, no failing test, and `fireOnce` only marks a
 * conversion sent on real dispatch, so no bad state accumulates to notice
 * later either. A renamed event name is the CAUGHT case (the specs below
 * assert both literals); this is the uncaught one. If ad-platform conversion
 * volume goes to zero, check this var before anything else.
 */

export type DupeSearchType = 'text' | 'image' | 'url_prefix'

const firedConversions = new Set<string>()
const STORAGE_PREFIX = 'google-ads-conversion:'

const hasFired = (key: string): boolean => {
  if (firedConversions.has(key)) return true

  try {
    return window.sessionStorage.getItem(`${STORAGE_PREFIX}${key}`) !== null
  } catch {
    // sessionStorage can be unavailable in privacy-restricted browsers. The
    // module-level set still prevents duplicate React effects in that case.
    return false
  }
}

/**
 * Only ever called once `zarazTrack` reports it reached `zaraz.track()`. The
 * mark is session-durable, so writing it on a no-op (rollout flag off, or the
 * edge script not attached yet) would suppress the conversion for the rest of
 * the session with no way to retry.
 */
const markFired = (key: string): void => {
  firedConversions.add(key)

  try {
    window.sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, '1')
  } catch {
    // See hasFired: privacy-restricted browsers throw here.
  }
}

/**
 * Guard, dispatch, and mark — in that order, in one place. The ordering IS the
 * safety property (a mark written on a drop suppresses that conversion for the
 * whole session), so it lives here rather than being restated per event.
 *
 * Two known limits, both accepted rather than overlooked:
 *
 * `zarazTrack` returns true when `zaraz.track()` did not throw, which is not a
 * promise the beacon left the browser. A conversion dispatched from the
 * `pagehide` flush during a click-out can have its `/t` POST aborted and still
 * be marked sent, losing it for the session. Firing without marking would trade
 * that loss for a double-count on any return visit; neither is free, and the
 * loss is the conservative direction.
 *
 * `transaction_id` dedupe is documented for GA4 ecommerce events, not arbitrary
 * custom ones, so Google may not collapse repeats on it at all. Nothing here
 * depends on it for correctness: chat mints a unique id per search either way,
 * and the results-page key at worst double-counts one person's repeat search on
 * a surface carrying ~128 searches a week.
 */
const fireOnce = (
  event: string,
  transactionId: string,
  extraParams?: Record<string, unknown>
): void => {
  const key = `${event}:${transactionId}`
  if (hasFired(key)) return

  // `transaction_id` last: it is the key this function just deduped on, so a
  // caller's extras must never be able to overwrite it and leave the stored
  // mark disagreeing with what went on the wire.
  //
  // `event_id` also rides along as its own field: TikTok's managed component
  // dedupes on `event_id` + `pixel_code`. Since DUP-2261 the only TikTok
  // consumer of THIS module is account creation; the search conversion has
  // its own daily event in `tiktokConversions.ts`.
  //
  // ⚠️ Meta does not read this Zaraz field, but it DOES share this id, and it
  // must stay that way. Meta's two transports fire separately from the same
  // call sites — the browser pixel (`metaConversions.ts`, as `eventID`) and
  // the CAPI event (`metaAdsConversion.ts`, as an `event_id` property) — and
  // both stamp this same `transactionId`. That shared value is the only thing
  // stopping Meta from counting one search as several conversions. Changing
  // how this id is derived breaks that dedup silently, in Meta's reporting
  // rather than in a test. If a Meta Zaraz managed component is ever built
  // after all, this field is already dedup-ready.
  //
  // It rides on EVERY conversion here, including ones with no per-platform
  // dedup consumer today (account creation): the id is per-transaction and
  // inert where unused, and stamping it uniformly means a conversion that
  // later gains one is already dedup-ready instead of needing this file
  // touched again.
  const dispatched = zarazTrack(event, {
    ...extraParams,
    transaction_id: transactionId,
    event_id: transactionId,
  })
  if (dispatched) markFired(key)
}

/**
 * Per-search ad conversion (DUP-2080, Google Ads). TikTok rode this event from
 * DUP-2125 until DUP-2261, when it moved to the per-day `dupe_performed_daily`.
 *
 * Fired from the two enrolled dispatch points — `useTrackPerformedDupe.ts`
 * (results page `/[hash]`) and `BrainChat.tsx` (chat, including the
 * `dupeMaxxServed` branch) — so all four entry paths are covered: text query,
 * image upload, `dupe.com/<merchant-url>`, and Dupe Max. The TikTok and Meta
 * daily conversions fire from the same two places, right after this one.
 *
 * ⚠️ The extension sidepanel results surface (`DupeResultsPage.tsx`) calls the
 * same hook and is deliberately NOT enrolled — it does not pass
 * `trackAdsConversions`, because it is not ads landing traffic (the reasoning
 * is at `useTrackPerformedDupe.ts`, on the `trackAdsConversions` prop). Its
 * searches are therefore absent from EVERY ad platform, by design. Two
 * consequences: don't file that as a bug, and don't read it as undercounting
 * when reconciling — our internal `Performed Dupe` count INCLUDES the
 * sidepanel while the ad platforms' counts cannot, so a gap is expected.
 *
 * ⚠️ Volume is lopsided between the two dispatch points: `/[hash]` carries
 * ~128 `Performed Dupe`/wk against chat's ~126k (see the comment at
 * `pages/[hash]/index.tsx`). Anything that only holds on one of them holds for
 * ~0.1% of the conversions.
 */
export const trackGoogleAdsDupePerformed = ({
  searchId,
  searchType,
}: {
  searchId: string
  searchType: DupeSearchType
}): void => {
  fireOnce('dupe_performed_manual2', searchId, { search_type: searchType })
}

/**
 * Per-account ad conversion, after the API confirms a NEW account
 * (DUP-2080 for Google, DUP-2125 for TikTok).
 *
 * Called from `AuthProvider.tsx` gated on `registerResult.existing === false`,
 * so it covers OAuth/social signup and cannot fire on a returning login or a
 * failed signup attempt. `userId` is the firebase auth uid, which makes it
 * once-per-account by construction.
 */
export const trackGoogleAdsAccountCreation = (userId: string): void => {
  fireOnce('dupe_account_creation2', userId)
}
